import { useContext } from 'react';
import { ApiClientContext } from 'common/contexts/api-client';
import { ApprovalContext } from 'pages/PostInspectionFlow/steps/Improvements/Approval/ApprovalContext';
import { useCreditCardFeeForAmount } from 'pages/Accounts/Components/FailedPayments/use-failed-payment-credit-card-fees';
import { usePaygPaymentMethod } from './use-payg-payment-method';

type PaygAmounts = {
  total: number;
  upfront: number;
  remaining: number;
  fee: number;
  totalDueNow: number;
  hasAmounts: boolean;
};

export function usePaygAmounts(): PaygAmounts {
  const { homeownerPayment, homeownerReportPayment, upfrontPayment } =
    useContext(ApprovalContext);
  const apiClient = useContext(ApiClientContext);
  const paymentMethod = usePaygPaymentMethod();

  const total = homeownerReportPayment?.total ?? 0;
  const estimatedUpfront = homeownerPayment?.estimatedUpfrontAmount ?? 0;

  const feeQuote = useCreditCardFeeForAmount({
    apiClient,
    paymentMethod: paymentMethod
      ? {
          paymentMethod: paymentMethod.paymentMethod,
          isVerified: paymentMethod.isVerified,
          isDomestic: paymentMethod.isDomestic,
          isInternational: paymentMethod.isInternational,
        }
      : null,
    amount: estimatedUpfront,
  });

  if (upfrontPayment) {
    return {
      total,
      upfront: upfrontPayment.amount,
      remaining: Math.max(total - upfrontPayment.amount, 0),
      fee: upfrontPayment.processingFee,
      totalDueNow: upfrontPayment.total,
      hasAmounts: true,
    };
  }

  return {
    total,
    upfront: estimatedUpfront,
    remaining: Math.max(total - estimatedUpfront, 0),
    fee: feeQuote?.feeAmount ?? 0,
    totalDueNow: feeQuote?.totalWithFee ?? estimatedUpfront,
    hasAmounts: Boolean(homeownerPayment && homeownerReportPayment),
  };
}
